import React, { Fragment } from 'react'
import { context, safeJsonParse } from '@tools/wr-catalog-base'
import { getDuration } from '../../product-utils'
import logo from '../../../assets/logo.png'
import { get as safeGet } from 'lodash'
import { fetchImage } from '../../html'
import { isNullOrEmptyString } from '@routes/List/utils'

const IMAGE_HOST =
  process.env.NODE_ENV === 'development' ? 'https://ptc-v2.catalogs.dev.workrails.com' : window.location.origin

const formatDate = (date, offsetDays = 0) => {
  date.setDate(date.getDate() + offsetDays)
  const day = date.getDate()
  const month = date.toLocaleString('default', { month: 'long' })
  const year = date.getFullYear()
  return `${month} ${day}, ${year}`
}

const getTextField = (item, fieldTag) => {
  const row = item.rows.find((r) => r.options.tag === fieldTag)
  return (row && safeJsonParse(row.control, row.control)) || ''
}

export default async function CatalogExportTemplate(props) {
  const { cart, cartTotals, formValues, region, asQuoteWord } = props

  let images = [`${IMAGE_HOST}/sow-assets/success-services-cover.jpg`]
  if (asQuoteWord) {
    images = await Promise.all(images.map(fetchImage))
  }

  const date = new Date()
  const monthName = date.toLocaleString('default', { month: 'long' })

  // Utility to insert the first arg of the condition if we're building a docx, inserts the second option otherwise
  const W = (whenTrue, otherwise = undefined) => (asQuoteWord ? whenTrue : otherwise)
  const pageBreak = W(
    <br style={{ pageBreakBefore: 'always', clear: 'both' }} />,
    <div style={{ pageBreakBefore: 'always' }} />,
  )

  return (
    <html>
      <head>
        <title>PTC Catalog Export.pdf</title>
        <style>{props.css}</style>
      </head>
      <body>
        {W(<style>{props.css}</style>)}
        <div className="page title-page" style={W(null, { backgroundImage: `url(${images[0]})` })}>
          {W(
            <img
              hspace="0"
              style={{ width: '210mm', height: '297mm', margin: 0, padding: 0, bottom: 0, left: 0 }}
              src={images[0]}
            />,
          )}
          <div className="title-backdrop" {...W({ style: { width: '105mm', left: '105mm' } })}>
            <div className="title-backdrop-stripes" />
            <div className="title-backdrop-layout">
              <div>
                <div className="title-header">
                  <img
                    src={`${IMAGE_HOST}${logo}`}
                    alt="brand logo"
                    className="title-header-logo"
                    {...W({
                      style: {
                        width: '56px',
                        height: '22px',
                        display: 'inline-block',
                      },
                    })}
                  />
                  <div className="title-header-text">
                    customer
                    <br />
                    success
                  </div>
                </div>
                <div className="title">
                  SUCCESS SERVICE PROPOSAL
                  <div className="subtitle" {...W({ style: { fontSize: '32px' } })}>
                    PTC Customer Success
                  </div>
                </div>
              </div>
              <div className="title-backdrop-footer">
                {formValues.company}
                <br />
                <span className="date" {...W({ style: { fontSize: '26px' } })}>
                  {monthName} {date.getDate()}, {date.getFullYear()}
                </span>
              </div>
            </div>
          </div>

          <div className="cover-upper-line" />
          <div className="cover-lower-line" />
        </div>
        <div className="page" {...W({ style: { margin: '0 50px' } })}>
          {W(
            <Fragment>
              <br />
              <br />
            </Fragment>,
          )}
          <h1>SUCCESS SERVICE SUMMARY</h1>
          <h2>About PTC Customer Success</h2>
          <p>Unlock more value and maximize results with success services.</p>
          <p>
            PTC Customer Success leverages deep domain expertise, and tight integrations with R&amp;D to provide our
            customers with solutions to outperform their competition. These services have been refined through years of
            delivery to customers just like you.
          </p>
          <h1>SUMMARY DETAILS</h1>
          <p>
            Proposal Date: <span className="text--active">{formatDate(new Date())}</span>
          </p>
          <p>
            Proposal Expiration Date: <span className="text--active">{formatDate(new Date(), 60)}</span>
          </p>
          <p>
            Delivery Region: <span className="text--active">{region}</span>
          </p>
          <p>
            Total Success Points: <span className="text--active">{cartTotals.totalPoints}</span>
          </p>
          <p>
            Approximate Duration: <span className="text--active">{cartTotals.totalHours} hours</span>
          </p>
          <p>
            We created a list of recommendations personalized for you. Review their details on the proceeding pages.
          </p>
          {W(pageBreak)}
        </div>
        <table style={{ margin: '0 50px' }}>
          <thead>
            <tr>
              <td>
                <div style={{ height: 100 }}>&nbsp;</div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td {...W({ style: { margin: '0 50px' } })}>
                {cart.map((item, i) => {
                  const regionName = context.utils.getRegionName()
                  const pricingRow = item.rows.find((r) => r.rowType === 'component:pricing')
                  const points = context.utils.product.getPointPrice(item)
                  const hours = getDuration(item)
                  let name = item.name
                  const ptcUrl = item.rows.find((row) => String(row.options.tag).toLowerCase() === '<ptc url>')
                  if (ptcUrl) {
                    const div = document.createElement('div')
                    div.innerHTML = ptcUrl.control
                    name = (
                      <a href={div.textContent.trim()} target="_blank" rel="noopener noreferrer">
                        {name}
                      </a>
                    )
                  }

                  return (
                    <Fragment key={i}>
                      {!!i && <div style={{ pageBreakBefore: 'always' }} />}
                      <h1>{name}</h1>
                      <h2>
                        {points
                          ? `${points} points | ${hours} hours`
                          : !!pricingRow &&
                            (isNullOrEmptyString(
                              pricingRow.control.constants.find((cons) => cons.name === regionName)?.value,
                            )
                              ? safeGet(pricingRow.control, 'schedule.serviceRateLabel')
                              : 'Not available in Region')}
                      </h2>
                      {W(
                        null,
                        <Fragment>
                          <br />
                          <br />
                        </Fragment>,
                      )}
                      <div dangerouslySetInnerHTML={{ __html: getTextField(item, '<Outcome>') }} />
                      <div className="separator" />
                      <h2>HOW IT WORKS</h2>
                      <div dangerouslySetInnerHTML={{ __html: getTextField(item, '<How It Works>') }} />
                      <div className="separator" />
                      <h2>PREREQUISITES</h2>
                      <div dangerouslySetInnerHTML={{ __html: getTextField(item, '<Prerequisites>') }} />
                      {cart.length !== i + 1 && (
                        <Fragment>
                          <br />
                          <br />
                        </Fragment>
                      )}
                    </Fragment>
                  )
                })}
              </td>
            </tr>
          </tbody>
          {W(
            null,
            <tfoot>
              <tr>
                <td>
                  <div style={{ height: 70 }}>&nbsp;</div>
                </td>
              </tr>
            </tfoot>,
          )}
        </table>
        <div className="doc-footer">
          <div className="doc-footer-header" />
          <div className="doc-footer-left-notch" />
          <div className="doc-footer-footer">
            <div>
              <span className="text--active">Page&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span> | Success Service Proposal
            </div>
            <div>
              <a href="https://www.ptc.com/success-services">www.ptc.com/success-services</a>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
